import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Vote, CheckCircle2, Clock, ShieldCheck, AlertCircle,
  BarChart3, Calendar, Users, Lock, Eye, ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

function fmtDt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (e) { return iso; }
}

const STATUS_META = {
  draft:     { label: "Draft",     color: "bg-slate-100 text-slate-600 border-slate-300" },
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700 border-blue-300" },
  live:      { label: "Live now",  color: "bg-emerald-100 text-emerald-700 border-emerald-300 animate-pulse" },
  closed:    { label: "Closed",    color: "bg-slate-100 text-slate-500 border-slate-300" },
  cancelled: { label: "Cancelled", color: "bg-rose-100 text-rose-700 border-rose-300" },
  deleted:   { label: "Removed",   color: "bg-slate-100 text-slate-400 border-slate-200" },
};

const TYPE_META = {
  committee: "Committee election",
  poll:      "Community poll",
  amendment: "Constitutional amendment",
  budget:    "Budget approval",
  yesno:     "Yes / No / Abstain",
  feedback:  "Feedback survey",
};

const ELIGIBILITY_META = {
  all_members:     "Every member",
  paid_only:       "Paid members",
  voting_eligible: "Voting-eligible (90+ days)",
  admins_only:     "Admins only",
};

// ──────────────────────────────────────────────────────────────────────────────
// Home — list elections
// ──────────────────────────────────────────────────────────────────────────────
export function ElectionsHome() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/elections")
      .then((r) => setRows(r.data.elections || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const live = rows.filter((r) => r.status === "live");
  const scheduled = rows.filter((r) => r.status === "scheduled");
  const closed = rows.filter((r) => r.status === "closed");

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1400px] mx-auto" data-testid="elections-home">
      <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Democratic governance</p>
          <h1 className="font-display text-4xl text-navy">Elections & polls</h1>
          <p className="mt-2 text-navy/65 max-w-2xl">
            Have your say in how ABGN-UK is run. Every vote is anonymous and
            cryptographically separated from your identity. One member, one vote.
          </p>
        </div>
      </div>

      {/* Trust pledge */}
      <div className="mt-6 bg-white border border-navy/10 rounded-xl p-4 flex items-start gap-3 max-w-3xl" data-testid="elections-trust-pledge">
        <ShieldCheck size={22} className="text-emerald-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-navy/75">
          <strong className="text-navy">Your vote is anonymous.</strong> When you
          cast a vote, we store your choice in one place and a tiny "you voted"
          marker in another. Your identity is never recorded alongside your
          choice. Even an ABGN-UK administrator with full database access
          cannot link you to a specific ballot.
        </div>
      </div>

      {loading ? (
        <div className="mt-10 p-12 text-center text-navy/55">Loading elections…</div>
      ) : rows.length === 0 ? (
        <div className="mt-10 bg-white border border-navy/10 rounded-xl p-12 text-center text-navy/55" data-testid="elections-empty">
          <Vote size={32} className="mx-auto mb-3 text-saffron" />
          No elections or polls yet. Watch this space — committee elections
          and community votes will be published here.
        </div>
      ) : (
        <>
          {live.length > 0 && <Section title="Live now" subtitle="Cast your vote before the deadline" rows={live} />}
          {scheduled.length > 0 && <Section title="Coming up" subtitle="Opens for voting soon" rows={scheduled} />}
          {closed.length > 0 && <Section title="Concluded" subtitle="View the results" rows={closed} />}
        </>
      )}
    </div>
  );
}

function Section({ title, subtitle, rows }) {
  return (
    <section className="mt-9">
      <div className="flex items-end justify-between border-b border-navy/10 pb-2 mb-4">
        <div>
          <h2 className="font-display text-2xl text-navy">{title}</h2>
          <p className="text-xs text-navy/55 mt-0.5">{subtitle}</p>
        </div>
        <span className="text-xs text-navy/55">{rows.length}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((e) => <ElectionCard key={e.election_id} election={e} />)}
      </div>
    </section>
  );
}

function ElectionCard({ election }) {
  const meta = STATUS_META[election.status] || STATUS_META.draft;
  return (
    <Link
      to={`/platform/elections/${election.election_id}`}
      data-testid={`election-card-${election.election_id}`}
      className="bg-white border border-navy/10 rounded-xl p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-saffron">{TYPE_META[election.election_type] || "Vote"}</span>
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm font-bold ${meta.color}`}>
          {meta.label}
        </span>
      </div>
      <h3 className="font-display text-lg text-navy mt-1.5 leading-tight">{election.title}</h3>
      <p className="text-sm text-navy/65 mt-1 line-clamp-2">{election.question}</p>
      <div className="mt-4 flex items-center justify-between text-xs text-navy/55 border-t border-navy/5 pt-3">
        <span className="flex items-center gap-1"><Users size={11} /> {ELIGIBILITY_META[election.eligibility]}</span>
        <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDt(election.end_at)}</span>
      </div>
    </Link>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Detail / Vote
// ──────────────────────────────────────────────────────────────────────────────
export function ElectionDetail() {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [election, setElection] = useState(null);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/elections/${electionId}`);
      setElection(r.data);
      // Try to fetch results — works if publish_results=live or closed
      try {
        const rs = await api.get(`/elections/${electionId}/results`);
        setResults(rs.data);
      } catch (e) { /* not viewable yet */ }
    } catch (e) {
      toast.error("Election not found");
      navigate("/platform/elections");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [electionId]);

  if (!election) return <div className="p-12 text-center text-navy/60">Loading…</div>;

  const meta = STATUS_META[election.status] || STATUS_META.draft;
  const isLive = election.status === "live";
  const canVote = election.can_vote && isLive && !election.has_voted;
  const maxChoices = election.max_choices || 1;

  const toggle = (oid) => {
    const next = new Set(selected);
    if (next.has(oid)) {
      next.delete(oid);
    } else {
      if (!election.allow_multiple) {
        next.clear();
      } else if (next.size >= maxChoices) {
        toast.error(`You may select at most ${maxChoices} option(s)`);
        return;
      }
      next.add(oid);
    }
    setSelected(next);
  };

  const cast = async () => {
    if (selected.size === 0) { toast.error("Please pick at least one option"); return; }
    setBusy(true);
    try {
      await api.post(`/elections/${electionId}/vote`, { choices: Array.from(selected) });
      toast.success("Your vote has been cast anonymously");
      setConfirming(false);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cast vote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-3xl mx-auto" data-testid="election-detail">
      <Link to="/platform/elections" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> All elections
      </Link>

      <article className="bg-white border border-navy/10 rounded-xl p-6 sm:p-8 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-widest text-saffron">{TYPE_META[election.election_type]}</span>
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm font-bold ${meta.color}`}>
            {meta.label}
          </span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-navy mt-2 leading-tight">{election.title}</h1>
        <div className="mt-2 text-xs text-navy/55 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Calendar size={11} /> Opens {fmtDt(election.start_at)}</span>
          <span className="flex items-center gap-1"><Clock size={11} /> Closes {fmtDt(election.end_at)}</span>
          <span className="flex items-center gap-1"><Users size={11} /> {ELIGIBILITY_META[election.eligibility]}</span>
        </div>
        {election.description && (
          <p className="mt-5 text-navy/80 font-serif leading-relaxed whitespace-pre-wrap">{election.description}</p>
        )}

        <div className="mt-7 bg-pearl border-l-4 border-saffron p-4 rounded-r-md">
          <div className="text-[10px] uppercase tracking-widest text-saffron">The question</div>
          <p className="mt-1 font-display text-xl text-navy leading-tight">{election.question}</p>
          {election.allow_multiple && (
            <p className="mt-1.5 text-xs text-navy/55">Multiple choice · pick up to {maxChoices} options</p>
          )}
        </div>

        {/* Already-voted state */}
        {election.has_voted && (
          <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3" data-testid="election-already-voted">
            <CheckCircle2 size={20} className="text-emerald-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-900">
              <strong>You voted on {fmtDt(election.cast_at)}.</strong><br />
              Your choice is anonymous and cannot be changed.
            </div>
          </div>
        )}

        {/* Ineligible */}
        {!election.can_vote && !election.has_voted && user && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="election-not-eligible">
            <Lock size={18} className="text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-900">
              You aren't currently eligible to vote in this election.
              <br />
              <span className="text-xs">Required: {ELIGIBILITY_META[election.eligibility]}.</span>
            </div>
          </div>
        )}

        {/* Login prompt */}
        {!user && (
          <div className="mt-5 bg-white border border-navy/15 rounded-md p-4 text-sm text-navy/75">
            <Link to="/login" className="text-saffron hover:text-saffron-light font-medium">Login</Link> to cast your vote.
          </div>
        )}

        {/* Live ballot */}
        {canVote && !confirming && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-navy/60 mb-2">Cast your vote</div>
            <div className="space-y-2">
              {(election.options || []).map((o) => {
                const isOn = selected.has(o.option_id);
                return (
                  <button
                    key={o.option_id}
                    onClick={() => toggle(o.option_id)}
                    type="button"
                    data-testid={`election-option-${o.option_id}`}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      isOn
                        ? "border-saffron bg-saffron/5"
                        : "border-navy/15 bg-white hover:border-saffron/50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isOn ? "border-saffron bg-saffron" : "border-navy/30"}`}>
                        {isOn && <CheckCircle2 size={12} className="text-white" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-navy">{o.label}</div>
                        {o.description && <div className="text-sm text-navy/65 mt-0.5">{o.description}</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-navy/55 flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-emerald-700" />
                Your vote will be recorded anonymously — once submitted it cannot be changed.
              </p>
              <button
                onClick={() => setConfirming(true)}
                disabled={selected.size === 0}
                data-testid="election-review-button"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40"
              >
                Review my ballot <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Confirmation modal-step */}
        {canVote && confirming && (
          <div className="mt-6 bg-saffron/5 border border-saffron/30 rounded-lg p-5" data-testid="election-confirm">
            <AlertCircle size={20} className="text-saffron" />
            <h4 className="font-display text-lg text-navy mt-2">Confirm your ballot</h4>
            <p className="text-sm text-navy/65 mt-1">Once submitted, your vote cannot be changed or withdrawn.</p>
            <ul className="mt-3 space-y-1.5">
              {(election.options || []).filter((o) => selected.has(o.option_id)).map((o) => (
                <li key={o.option_id} className="text-sm text-navy/85 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-700" /> {o.label}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="px-4 py-2 text-navy/65 hover:text-navy text-sm" data-testid="election-cancel-button">
                Back
              </button>
              <button onClick={cast} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light disabled:opacity-40" data-testid="election-confirm-button">
                <Vote size={14} /> {busy ? "Casting…" : "Cast my anonymous vote"}
              </button>
            </div>
          </div>
        )}
      </article>

      {/* Results — if available */}
      {results?.available && (
        <ElectionResults results={results} totalEligible={election.total_votes} />
      )}
      {results && !results.available && (
        <div className="mt-6 bg-white border border-navy/10 rounded-xl p-5 text-sm text-navy/65 flex items-start gap-2" data-testid="election-results-pending">
          <Eye size={16} className="mt-0.5" />
          <span>{results.reason}</span>
        </div>
      )}
    </div>
  );
}

function ElectionResults({ results }) {
  const top = results.options[0];
  return (
    <section className="mt-7 bg-white border border-navy/10 rounded-xl p-6 sm:p-8" data-testid="election-results">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-2xl text-navy flex items-center gap-2"><BarChart3 size={20} className="text-saffron" /> Results</h2>
        <span className="text-xs text-navy/55">{results.total_voters} {results.total_voters === 1 ? "vote" : "votes"}</span>
      </div>
      <div className="mt-5 space-y-3">
        {results.options.map((o, i) => {
          const leading = i === 0 && o.votes > 0;
          return (
            <div key={o.option_id} data-testid={`results-row-${o.option_id}`}>
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className={leading ? "font-medium text-navy" : "text-navy/85"}>{o.label}</span>
                <span className="font-mono text-navy/65 tabular-nums">{o.votes} · {o.percent}%</span>
              </div>
              <div className="mt-1 h-3 bg-pearl rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${leading ? "bg-saffron" : "bg-navy/60"}`}
                  style={{ width: `${Math.max(o.percent, 0)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-5 text-xs text-navy/55 italic border-t border-navy/5 pt-3">
        <ShieldCheck size={11} className="inline mr-1 text-emerald-700" />
        {results.anonymity_note}
      </p>
    </section>
  );
}
